import { FormCustomerConfirmation } from "./form-customer-confirmation";
import type { FormContactView } from "@/api";
import React from "react";
import { Banner, Button, Card, CollapsibleSection, Input, Textarea, TimelineItem } from "@/ds";
import { Icon, fmtStamp } from "@/lib";
import { ApiError, cockpit } from "@/api";
import type { AuditEvent, CustomerSuggestion, CustomerView, Fact, RequestDetail } from "@/api";
import {
  EMPTY_CUSTOMER_FIELD_EDITOR_PRESENCE,
  customerFieldEditorPresence,
  customerFieldEditAttr,
  customerFieldEditSelector,
  customerFieldInputId,
  customerFieldPatch,
  customerFieldRows,
  customerSuggestionKey,
  customerSuggestionTitleKey,
  type CustomerEditableField,
  type CustomerFieldRow,
} from "../customer-card.logic";
import { useT, type TFunc } from "../../i18n";
import { operatorRequestReferenceLabel } from "../screen-workspace.logic";
import { deliveryAuditAttemptIdentity } from "../delivery-reconciliation";
import { AuditPolicyMeta } from "../audit-policy-meta";
import { isEmptyVal, valToStr } from "../fact-value";
import { CustomerAddresses } from "./customer-addresses";

const AUDIT_ICON: Record<string, string> = {
  form_customer_confirmed: "user",
  request_assigned: "user",
  request_unassigned: "user",
  request_taken_over: "user",
  draft_generated: "sparkles",
  draft_created: "sparkles",
  draft_edited: "pencil",
  draft_approved: "thumbsUp",
  draft_rejected: "xCircle",
  draft_sent: "send",
  request_closed: "fileCheck",
  request_lifecycle_changed: "gauge",
  fact_overridden: "pencil",
  fact_lock_changed: "lock",
  decision_recomputed: "gauge",
  delivery_resolved: "checkCircle",
  delivery_resolved_sent: "checkCircle",
  delivery_resolved_failed: "checkCircle",
};

// Twice the visual exit duration: this is a completion failsafe, not a second motion timing.
const CUSTOMER_FIELD_EXIT_FALLBACK_MS = 240;

function factVal(facts: Fact[], path: string): unknown {
  return facts.find((f) => f.fieldPath === path)?.value ?? null;
}
// ============================ CLIENT ============================
/**
 * Kunde tab (#534, redesigned in #588): the persistent tenant-scoped customer
 * profile behind the request's trusted sender.
 *
 * The card starts from what the system already knows rather than from a blank
 * form. The server fills empty fields from the request's extracted contact
 * facts and returns, alongside the profile, the values the mail offers that the
 * profile does not hold. Each field therefore shows its value, where that value
 * came from, and — when the mail disagrees — a suggestion the operator accepts
 * or ignores. Editing is per field so an untouched field can never be clobbered
 * by a concurrent save; the optimistic lock (`expected_version`, 409 → reload)
 * is unchanged. Email stays the read-only identity, and requests without a
 * trusted sender fall back to request-local extracted values and never group.
 *
 * Every value is a white box like a row on the Fakten tab (#2015): label,
 * value, and — where the operator may change it — the pencil that opens the
 * editor in place.
 */
export function ClientPanel({ request, facts, messages, customerTypeFact, onCustomerConfirmed }: {
  request: RequestDetail["request"];
  facts: Fact[];
  messages: RequestDetail["messages"];
  customerTypeFact?: React.ReactNode;
  onCustomerConfirmed?: () => Promise<void>;
}) {
  const t = useT();
  const [customerState, setCustomerState] = React.useState<{
    loading: boolean;
    customer: CustomerView | null;
    suggestions: CustomerSuggestion[];
    untrusted: boolean;
    formContact?: FormContactView;
    error: string | null;
  }>({ loading: true, customer: null, suggestions: [], untrusted: false, error: null });
  const [editorPresence, dispatchEditorPresence] = React.useReducer(
    customerFieldEditorPresence,
    EMPTY_CUSTOMER_FIELD_EDITOR_PRESENCE,
  );
  const editingField = editorPresence.active;
  const presentedEditingField = editorPresence.presented;
  const [draft, setDraft] = React.useState("");
  // What the operator had typed when a concurrent save beat them to it. The
  // editor closes so they can see the record that actually won, but their text
  // is not thrown away — re-opening that field restores it.
  const [rejectedDraft, setRejectedDraft] = React.useState<{
    field: CustomerEditableField;
    value: string;
  } | null>(null);
  // Set by an action whose own control disappears with the write; consumed once
  // the re-render has put an enabled control back on the page.
  const [pendingFocus, setPendingFocus] = React.useState<CustomerEditableField | null>(null);
  React.useEffect(() => {
    if (pendingFocus === null) return;
    // Only recover focus the write dropped. A slow save gives the operator time
    // to click elsewhere, and stealing it back would be worse than the loss.
    // A control that went inert under the focus still reports as the active
    // element until the browser's next frame; it holds the focus no more than
    // the document does.
    const active = document.activeElement;
    if (active === null || active === document.body || (active as HTMLButtonElement).disabled === true) {
      document.querySelector<HTMLElement>(customerFieldEditSelector(pendingFocus))?.focus();
    }
    setPendingFocus(null);
  }, [pendingFocus]);
  const [busy, setBusy] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [conflictNotice, setConflictNotice] = React.useState(false);
  // Ignoring a suggestion is a view-local decision: nothing is written, and the
  // key carries the value so a later mail proposing something else is offered
  // again instead of inheriting this dismissal.
  const [dismissed, setDismissed] = React.useState<ReadonlySet<string>>(() => new Set());

  const customerLoadSequence = React.useRef(0);
  const activeRequest = React.useRef(request.id);
  activeRequest.current = request.id;
  const loadCustomer = React.useCallback(async () => {
    const sequence = ++customerLoadSequence.current;
    try {
      const result = await cockpit.getRequestCustomer(request.id);
      if (sequence !== customerLoadSequence.current || activeRequest.current !== request.id) return null;
      setCustomerState({
        loading: false,
        customer: result.customer,
        formContact: result.form_contact,
        suggestions: result.suggestions ?? [],
        untrusted: result.customer === null,
        error: null,
      });
      return result.customer;
    } catch (error: any) {
      if (sequence !== customerLoadSequence.current || activeRequest.current !== request.id) return null;
      setCustomerState({
        loading: false,
        customer: null,
        suggestions: [],
        untrusted: false,
        error: error?.message || String(error),
      });
      return null;
    }
  }, [request.id, request.version, request.current_revision]);
  React.useEffect(() => { void loadCustomer(); }, [loadCustomer]);

  const customer = customerState.customer;
  const startEditing = (field: CustomerEditableField) => {
    if (!customer || busy || (presentedEditingField !== null && presentedEditingField !== field)) {
      return;
    }
    // Reversing this field's exit keeps the mounted input and its draft. A
    // fresh open starts from the conflict draft or the authoritative record.
    if (presentedEditingField !== field) {
      setDraft(
        rejectedDraft?.field === field ? rejectedDraft.value : customer[field] ?? "",
      );
    }
    setRejectedDraft(null);
    setFormError(null);
    setConflictNotice(false);
    dispatchEditorPresence({ type: "begin", field });
  };
  const leaveEditing = (field: CustomerEditableField) => {
    dispatchEditorPresence({ type: "leave", field });
    // Cancel and Save go inert with the editor; the pencil that opened it
    // takes the focus back.
    setPendingFocus(field);
  };
  React.useEffect(() => {
    if (editingField !== null || presentedEditingField === null) return;
    const field = presentedEditingField;
    // transitionend is the primary completion signal. The matching timer
    // prevents stale editor DOM if styles are unavailable or the event is lost.
    const fallback = globalThis.setTimeout(() => {
      dispatchEditorPresence({ type: "finish-leave", field });
    }, CUSTOMER_FIELD_EXIT_FALLBACK_MS);
    return () => globalThis.clearTimeout(fallback);
  }, [editingField, presentedEditingField]);
  /**
   * Save one field. A conflict leaves the editor and shows the reloaded record,
   * which is the only state from which a deliberate re-apply makes sense, and
   * parks anything typed in `rejectedDraft`; any other error keeps the editor
   * open with the draft still in it.
   */
  const saveField = async (field: CustomerEditableField, raw: string) => {
    if (!customer || busy) return;
    // A save that changes nothing still travels when the operator does not yet
    // own the field: submitting an empty field empty is how they say "leave
    // this blank", and only the server can record that decision.
    const unchanged = (customer[field] ?? "") === raw.trim();
    if (unchanged && customer.field_sources?.[field] === "operator") {
      leaveEditing(field);
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      await cockpit.updateCustomer(customer.id, customerFieldPatch(field, raw, customer.version));
      await loadCustomer();
      leaveEditing(field);
    } catch (error: any) {
      if (error instanceof ApiError && error.status === 409) {
        await loadCustomer();
        leaveEditing(field);
        setRejectedDraft({ field, value: raw });
        setConflictNotice(true);
      } else {
        setFormError(error?.message || String(error));
      }
    } finally {
      setBusy(false);
    }
  };
  const restoreField = async (field: CustomerEditableField, value: string | null) => {
    // Restoring is an ordinary operator edit: same optimistic lock, same
    // ownership, and the replaced value becomes the next thing to undo.
    if (editingField !== null) return;
    await saveField(field, value ?? "");
    // The clicked control is disabled for the duration of the save, so the
    // browser has already blurred it — on failure as much as on success, and
    // a restore taken inside the card removes that control outright. Ask for
    // the focus move rather than doing it here: this continuation resumes
    // before React has committed `busy: false`, and a disabled control cannot
    // take focus.
    setPendingFocus(field);
  };
  const acceptSuggestion = async (suggestion: CustomerSuggestion) => {
    // Accepting saves at once, which would discard an open draft. The button is
    // already disabled while any editor is open — this restates the invariant
    // where the handler can see it, and is unreachable while that holds.
    if (editingField !== null) return;
    await saveField(suggestion.field, suggestion.value);
    // Accepting always removes the card the button lives in, so it loses focus
    // exactly like a restore does. Same trap, same recovery.
    setPendingFocus(suggestion.field);
  };
  const dismissSuggestion = (suggestion: CustomerSuggestion) => {
    // The button is inside the card it removes, so focus would land on the
    // document — mid sentence, if an editor is open. Move it first: React
    // unmounts after this handler, and both targets are guaranteed present,
    // because the button is only clickable in one of exactly these two states.
    const target = editingField === suggestion.field
      ? document.getElementById(customerFieldInputId(suggestion.field))
      : document.querySelector<HTMLElement>(customerFieldEditSelector(suggestion.field));
    target?.focus();
    setDismissed((current) => new Set(current).add(customerSuggestionKey(suggestion)));
  };

  const firstFact = (paths: string[]) => {
    for (const path of paths) {
      const fact = facts.find((candidate) => candidate.fieldPath === path);
      if (request.contact_form_source && !["extracted", "confirmed", "manually_overridden",
        "externally_resolved", "curated_venue_resolved"].includes(fact?.status ?? "")) continue;
      const value = fact?.value;
      if (!isEmptyVal(value)) return valToStr(value, t);
    }
    return null;
  };
  const inbound = messages
    .filter((message) => message.direction === "inbound")
    .slice()
    .sort((left, right) => Date.parse(right.receivedAt || "") - Date.parse(left.receivedAt || ""));
  const latestInbound = inbound[0] ?? null;
  const mailboxAddress = latestInbound?.toAddrs?.find(Boolean)
    ?? messages.find((message) => message.direction === "outbound")?.fromAddr
    ?? null;
  const reference = operatorRequestReferenceLabel(request.reference);

  const requestContext = (
    <>
      {reference && <CustomerFactBox label={t("ws.review.reference")}>{reference}</CustomerFactBox>}
      {latestInbound?.receivedAt && <CustomerFactBox label={t("ws.review.receivedAt")}>{fmtStamp(latestInbound.receivedAt)}</CustomerFactBox>}
      {mailboxAddress && <CustomerFactBox label={t("ws.review.mailbox")}>{mailboxAddress}</CustomerFactBox>}
    </>
  );

  if (customerState.loading) {
    return <>
      <Card title={t("ws.review.clientTitle")}><div className="muted" style={{ fontSize: "var(--text-sm)" }}>{t("common.loading")}</div></Card>
      <CustomerAddresses facts={facts} />
    </>;
  }

  if (!customer) {
    // No trusted sender identity (or the profile failed to load): request-local
    // extracted values only, nothing groups across requests.
    const company = firstFact(["customer.company_name", "customer.company"]);
    const contact = firstFact(["customer.contact_name", "customer.name"]);
    const phone = firstFact(["customer.phone", "customer.phone_number", "contact.phone"]);
    return (
      <>
        <Card title={t("ws.review.clientTitle")}>
          {customerState.error
            ? <div style={{ marginBottom: "var(--space-4)" }}><Banner tone="danger" title={t("common.error")}>{customerState.error}</Banner></div>
            : !customerState.formContact && <p className="muted" style={{ fontSize: "var(--text-sm)", marginTop: 0 }} data-testid="customer-untrusted-note">{t("ws.customer.untrusted")}</p>}
          <div className="cust-fields">
            {company && <CustomerFactBox label={t("ws.review.company")}>{company}</CustomerFactBox>}
            {contact && <CustomerFactBox label={t("ws.review.contact")}>{contact}</CustomerFactBox>}
            {phone && <CustomerFactBox label={t("ws.review.phone")}>{phone}</CustomerFactBox>}
            {(customerState.formContact?.source_address || latestInbound?.fromAddr) && <CustomerFactBox label={t(customerState.formContact ? "ws.form.source" : "ws.review.contactEmail")}>{customerState.formContact?.source_address || latestInbound?.fromAddr}</CustomerFactBox>}
            {requestContext}
          </div>
          {customerTypeFact}
          {customerState.formContact && <FormCustomerConfirmation
            requestId={request.id} contact={customerState.formContact}
            onChanged={async () => { await onCustomerConfirmed?.(); await loadCustomer(); }} />}
        </Card>
        <CustomerAddresses facts={facts} />
      </>
    );
  }

  const rows = customerFieldRows(customer, customerState.suggestions, dismissed);
  // One row at a time: switching away mid-edit would silently drop the draft.
  const otherRowLocked = (field: CustomerEditableField) =>
    busy || (presentedEditingField !== null && presentedEditingField !== field);
  // Accepting saves immediately, so it stays inert while ANY editor is open —
  // including its own row's, where it would discard the draft. Ignoring writes
  // nothing and only hides a card, so it follows the ordinary row lock.
  const acceptLocked = busy || presentedEditingField !== null;

  const renderField = (row: CustomerFieldRow) => (
    <CustomerFieldCard
      key={row.field}
      row={row}
      busy={busy}
      locked={otherRowLocked(row.field)}
      acceptLocked={acceptLocked}
      editing={editingField === row.field}
      editorPresent={presentedEditingField === row.field}
      draft={draft}
      onDraft={setDraft}
      onStartEdit={() => startEditing(row.field)}
      onCancelEdit={() => leaveEditing(row.field)}
      onEditorExited={() => dispatchEditorPresence({ type: "finish-leave", field: row.field })}
      onSave={() => { void saveField(row.field, draft); }}
      onRestore={(value) => { void restoreField(row.field, value); }}
      onAccept={(suggestion) => { void acceptSuggestion(suggestion); }}
      onDismiss={dismissSuggestion}
    />
  );

  return (
    <Card title={t("ws.review.clientTitle")}>
      {conflictNotice && (
        <div style={{ marginBottom: "var(--space-4)" }} data-testid="customer-conflict">
          <Banner tone="danger" title={t("common.error")}>{t("ws.customer.conflict")}</Banner>
        </div>
      )}
      {formError && (
        <div style={{ marginBottom: "var(--space-4)" }}>
          <Banner tone="danger" title={t("common.error")}>{formError}</Banner>
        </div>
      )}
      <div className="cust-profile" data-testid="customer-card">
        <div className="cust-fields" data-testid="customer-fields">
          {rows.filter((row) => row.field === "name" || row.field === "company").map(renderField)}
          <CustomerFactBox label={t("ws.review.contactEmail")} testId="customer-email">{customer.email}</CustomerFactBox>
          {rows.filter((row) => row.field === "phone").map(renderField)}
        </div>
        {customerTypeFact}
        <CustomerAddresses facts={facts} embedded />
        <div className="cust-profile__notes">
          {rows.filter((row) => row.field === "notes").map(renderField)}
        </div>
        <section className="cust-profile__context" aria-label={t("ws.customer.requestContext")} data-testid="customer-context">
          <h3 className="cust-profile__heading">{t("ws.customer.requestContext")}</h3>
          <div className="cust-fields">
            {customerState.formContact?.source_address && <CustomerFactBox label={t("ws.form.source")}>{customerState.formContact.source_address}</CustomerFactBox>}
            {customer.first_contact_at && <CustomerFactBox label={t("ws.customer.firstContact")}>{fmtStamp(customer.first_contact_at)}</CustomerFactBox>}
            {requestContext}
          </div>
        </section>
      </div>
    </Card>
  );
}

/** Cut by code point, so an emoji at the boundary cannot become a lone surrogate. */
function truncateForLabel(value: string, max: number): string {
  const points = [...value];
  return points.length > max ? `${points.slice(0, max).join("")}…` : value;
}

/** Names the value the undo puts back, so the operator never guesses (#606). */
function restoreLabel(value: string | null, t: TFunc): string {
  return value === null
    ? t("ws.customer.restoreEmpty")
    : t("ws.customer.restoreValue", { value: truncateForLabel(value, 40) });
}

/** A value the operator reads but does not change here: the fact box without tools. */
function CustomerFactBox({ label, testId, children }: { label: string; testId?: string; children: React.ReactNode }) {
  return (
    <div className="fact fact-row cust-field" data-testid={testId}>
      <div className="fact-row__line">
        <div className="cust-field__body">
          <span className="fact-row__content">
            <span className="fact-row__label">{label}</span>
            <span className="fact-row__value">{children}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

const CUSTOMER_FIELD_LABEL_KEYS: Record<CustomerEditableField, string> = {
  name: "ws.review.contact",
  company: "ws.review.company",
  phone: "ws.review.phone",
  notes: "ws.customer.notes",
};

/** One profile field: value, provenance, inline editor, and what the request offers (#588). */
function CustomerFieldCard({ row, busy, locked, acceptLocked, editing, editorPresent, draft, onDraft, onStartEdit, onCancelEdit, onEditorExited, onSave, onRestore, onAccept, onDismiss }: {
  row: CustomerFieldRow;
  busy: boolean;
  /** Another row owns the editor (or a save is in flight): this row is inert. */
  locked: boolean;
  /** Any editor is open: accepting would throw the open draft away. */
  acceptLocked: boolean;
  editing: boolean;
  /** The editor remains mounted while its exit transition is in flight. */
  editorPresent: boolean;
  draft: string;
  onDraft: (value: string) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onEditorExited: () => void;
  onSave: () => void;
  onRestore: (value: string | null) => void;
  onAccept: (suggestion: CustomerSuggestion) => void;
  onDismiss: (suggestion: CustomerSuggestion) => void;
}) {
  const t = useT();
  const label = t(CUSTOMER_FIELD_LABEL_KEYS[row.field]);
  const inputId = customerFieldInputId(row.field);
  const editLabel = row.value === null ? t("ws.customer.addValue") : t("ws.customer.edit");
  // The card carries the undo when the mail names the same value; otherwise it
  // sits beside Edit. Neither is actionable while this editor is presented,
  // because the operator can type the value directly instead.
  const cardRestore = row.restoreIsSuggestion ? row.restore : null;
  const inlineRestore = row.restoreIsSuggestion ? null : row.restore;
  const suggestion = row.suggestion;
  const editorRef = React.useRef<HTMLDivElement | null>(null);
  // The pencil opens the editor ready to type in: the button itself is gone
  // with the click.
  React.useEffect(() => {
    if (editing) document.getElementById(inputId)?.focus();
  }, [editing, inputId]);
  React.useEffect(() => {
    const node = editorRef.current;
    if (!node || editing) return;
    const onTransitionCancel = (event: TransitionEvent) => {
      if (event.target !== node || event.propertyName !== "opacity") return;
      onEditorExited();
    };
    node.addEventListener("transitioncancel", onTransitionCancel);
    return () => node.removeEventListener("transitioncancel", onTransitionCancel);
  }, [editing, editorPresent, onEditorExited]);
  return (
    <div className="fact fact-row cust-field" data-testid="customer-field" data-field={row.field}>
      <div className="cust-field__presence">
        {!editing && (
          <div className="fact-row__line" data-testid="customer-field-read">
            <div className="cust-field__body">
              <span className="fact-row__content">
                <span className="fact-row__label">{label}</span>
                <span className="fact-row__value" data-testid="customer-value">
                  {row.value ?? <span className="muted">{t("ws.customer.empty")}</span>}
                </span>
              </span>
              {row.provenanceKey && (
                <span className="cust-field__origin" data-testid="customer-provenance">{t(row.provenanceKey)}</span>
              )}
            </div>
            <span className="fact-row__tools cust-field__controls">
              {inlineRestore && (
                <button
                  type="button"
                  className="linkbtn"
                  disabled={acceptLocked}
                  aria-label={`${restoreLabel(inlineRestore.value, t)} — ${label}`}
                  data-testid={`customer-restore-${row.field}`}
                  onClick={() => onRestore(inlineRestore.value)}
                >{restoreLabel(inlineRestore.value, t)}</button>
              )}
              {/* The same pencil as a fact row's (#2015); the name is spoken and shown on hover. */}
              <button
                type="button"
                className="iconbtn fact-row__tool"
                {...customerFieldEditAttr(row.field)}
                disabled={locked}
                aria-label={`${editLabel} — ${label}`}
                title={editLabel}
                data-testid={`customer-edit-${row.field}`}
                onClick={onStartEdit}
              ><Icon name="pencil" size={14} /></button>
            </span>
          </div>
        )}
        {editorPresent && (
          <div
            ref={editorRef}
            className="cust-field__edit cust-field__editor-presence"
            data-testid="customer-field-editor"
            data-transition-state={editing ? "entered" : "exiting"}
            aria-hidden={editing ? undefined : true}
            onTransitionEnd={(event) => {
              if (!editing && event.currentTarget === event.target && event.propertyName === "opacity") {
                onEditorExited();
              }
            }}
          >
            <label className="fact-row__label" htmlFor={inputId}>{label}</label>
            {row.field === "notes"
              ? <Textarea id={inputId} rows={4} value={draft} disabled={busy || !editing} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onDraft(e.target.value)} />
              : <Input id={inputId} value={draft} disabled={busy || !editing} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onDraft(e.target.value)} />}
            <div className="cust-field__actions">
              <Button variant="ghost" size="sm" disabled={busy || !editing} onClick={onCancelEdit}>{t("common.cancel")}</Button>
              <Button loading={busy} size="sm" disabled={busy || !editing} data-testid="customer-field-save" onClick={onSave}>
                {t("ws.customer.save")}
              </Button>
            </div>
          </div>
        )}
      </div>
      {suggestion && (
        <div className="cust-suggest" data-testid="customer-suggestion" data-field={row.field}>
          <div className="cust-suggest__head">{t(customerSuggestionTitleKey(suggestion))}</div>
          <div className="cust-suggest__value" data-testid="customer-suggestion-value">{suggestion.value}</div>
          {suggestion.evidence_text && (
            <div className="cust-suggest__evidence">„{suggestion.evidence_text}“</div>
          )}
          <div className="cust-field__actions">
            {cardRestore
              ? (
                <Button
                  size="sm"
                  disabled={acceptLocked}
                  aria-label={`${restoreLabel(cardRestore.value, t)} — ${label}`}
                  data-testid={`customer-restore-${row.field}`}
                  onClick={() => onRestore(cardRestore.value)}
                >{restoreLabel(cardRestore.value, t)}</Button>
              )
              : (
                <Button
                  size="sm"
                  disabled={acceptLocked}
                  aria-label={`${t("ws.customer.acceptSuggestion")} — ${label}`}
                  data-testid="customer-suggestion-accept"
                  onClick={() => onAccept(suggestion)}
                >{t("ws.customer.acceptSuggestion")}</Button>
              )}
            <Button
              variant="ghost"
              size="sm"
              disabled={locked}
              aria-label={`${t("ws.customer.ignoreSuggestion")} — ${label}`}
              data-testid="customer-suggestion-dismiss"
              onClick={() => onDismiss(suggestion)}
            >{t("ws.customer.ignoreSuggestion")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================ AUDIT-VERLAUF ============================
export function AuditPanel({ audit }: { audit?: AuditEvent[] }) {
  const t = useT();
  if (!audit || !audit.length) return null;
  return (
    <section className="ws-secondary-section ws-audit-section">
      <CollapsibleSection summary={t("ws.auditTrail")} count={t("ws.eventsCount", { n: audit.length })}>
        <div style={{ paddingTop: "var(--space-2)" }}>
          {audit.map((a) => {
            const icon = AUDIT_ICON[a.event_type] || "history";
            const verb = t(`audit.${a.event_type}`, undefined, t("ws.auditEvent"));
            const deliveryIdentity = deliveryAuditAttemptIdentity(a.payload);
            const recipientChange = (a.payload as { recipientChange?: { from?: string; to?: string } } | null)?.recipientChange;
            const meta = recipientChange
              ? <span>{t("ws.recipient.history", { from: recipientChange.from ?? "—", to: recipientChange.to ?? "—" })}</span>
              : deliveryIdentity
              ? <span className="neo-mono">{t("delivery.auditIdentity", deliveryIdentity)}</span>
              : <AuditPolicyMeta audit={a} t={t} />;
            return (
              <TimelineItem key={a.id} type="audit" actor={a.actor_email || t("ws.system")} verb={verb}
                time={a.created_at ? fmtStamp(a.created_at) : ""} icon={<Icon name={icon} size={14} />}
                meta={meta} />
            );
          })}
        </div>
      </CollapsibleSection>
    </section>
  );
}
